import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Save, Loader2 } from "lucide-react";
import { Controller } from "react-hook-form";
import { useEditPage } from "@/hooks/useEditPage";
import ErrorBanner from "@/components/common/ErrorBanner";
import SaveSuccessIndicator from "@/components/common/SaveSuccessIndicator";
import { RichTextEditor } from "@/components/editor/RichTextEditor";
import i18n from "@/i18n";

interface SeasonMonth {
  score: "best" | "good" | "poor";
  note?: string;
}

interface CountryForm {
  countryCode: string;
  countryName: string;
  visaPolicy: string;
  visaFreeDays: number | null;
  visaFee: string;
  visaUrl: string;
  visaNotes: string;
  passportMinValidityMonths: number | null;
  vaccinationRequired: string;
  healthNotes: string;
  arrivalCard: string;
  arrivalCardUrl: string;
  arrivalCardWindowDays: number | null;
  arrivalCardNotes: string;
  arrivalCardGroupMax: number | null;
  arrivalCardArtifactsText: string;
  arrivalCardChannel: string;
  arrivalCardAutoEnabled: boolean;
  visaFilingLeadDays: number | null;
  seasonality: Record<string, SeasonMonth> | null;
  currencyInfo: string;
  plugs: string;
  tipping: string;
  safetyNotes: string;
  practicalNotes: string;
  isActive: boolean;
}

const EMPTY: CountryForm = {
  countryCode: "",
  countryName: "",
  visaPolicy: "",
  visaFreeDays: null,
  visaFee: "",
  visaUrl: "",
  visaNotes: "",
  passportMinValidityMonths: null,
  vaccinationRequired: "",
  healthNotes: "",
  arrivalCard: "",
  arrivalCardUrl: "",
  arrivalCardWindowDays: null,
  arrivalCardNotes: "",
  arrivalCardGroupMax: null,
  arrivalCardArtifactsText: "",
  arrivalCardChannel: "manual",
  arrivalCardAutoEnabled: false,
  visaFilingLeadDays: null,
  seasonality: null,
  currencyInfo: "",
  plugs: "",
  tipping: "",
  safetyNotes: "",
  practicalNotes: "",
  isActive: true,
};

const MONTHS = [
  i18n.t("catalog.months.jan"),
  i18n.t("catalog.months.feb"),
  i18n.t("catalog.months.mar"),
  i18n.t("catalog.months.apr"),
  i18n.t("catalog.months.may"),
  i18n.t("catalog.months.jun"),
  i18n.t("catalog.months.jul"),
  i18n.t("catalog.months.aug"),
  i18n.t("catalog.months.sep"),
  i18n.t("catalog.months.oct"),
  i18n.t("catalog.months.nov"),
  i18n.t("catalog.months.dec"),
];

const SCORE_STYLE: Record<string, string> = {
  best: "bg-green-100 text-green-700 border-green-300 dark:bg-green-900/30 dark:text-green-300",
  good: "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-300",
  poor: "bg-red-50 text-red-600 border-red-200 dark:bg-red-900/20 dark:text-red-300",
};

const SCORE_LABELS: Record<SeasonMonth["score"], string> = {
  best: i18n.t("catalog.countryKb.edit.scoreBest"),
  good: i18n.t("catalog.countryKb.edit.scoreGood"),
  poor: i18n.t("catalog.countryKb.edit.scorePoor"),
};

/** 12-month seasonality board: click a month to cycle best → good → poor → unset. */
function SeasonalityEditor({
  value,
  onChange,
}: {
  value: Record<string, SeasonMonth> | null;
  onChange: (v: Record<string, SeasonMonth> | null) => void;
}) {
  const { t } = useTranslation();
  const cycle = (m: number) => {
    const key = String(m);
    const cur = value?.[key]?.score;
    const next =
      cur === "best" ? "good" : cur === "good" ? "poor" : cur === "poor" ? undefined : "best";
    const out = { ...(value ?? {}) };
    if (next) out[key] = { ...(out[key] ?? {}), score: next };
    else delete out[key];
    onChange(Object.keys(out).length ? out : null);
  };
  const setNote = (m: number, note: string) => {
    const key = String(m);
    if (!value?.[key]) return;
    onChange({ ...value, [key]: { ...value[key], note } });
  };

  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
      {MONTHS.map((name, i) => {
        const m = i + 1;
        const entry = value?.[String(m)];
        return (
          <div
            key={m}
            className={`rounded-lg border p-2 ${
              entry ? SCORE_STYLE[entry.score] : "border-gray-200 dark:border-gray-700"
            }`}
          >
            <button
              type="button"
              onClick={() => cycle(m)}
              className="flex w-full items-center justify-between text-sm font-medium"
              title={t("catalog.countryKb.edit.cycleTitle")}
            >
              <span>{name}</span>
              <span className="text-xs uppercase">{entry ? SCORE_LABELS[entry.score] : "—"}</span>
            </button>
            {entry && (
              <input
                value={entry.note ?? ""}
                onChange={(e) => setNote(m, e.target.value)}
                placeholder={t("catalog.countryKb.edit.seasonNotePlaceholder")}
                className="mt-1 w-full rounded border-0 bg-white/60 px-1.5 py-0.5 text-xs text-gray-700 placeholder:text-gray-400 dark:bg-black/20 dark:text-gray-200"
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Country KB edit page (Block 5.6 Řez 1) — visa / passport & health / arrival card /
 * seasonality / practical. Numbers and enums stay structured (the five consumers read
 * them as data); only the notes fields are rich text.
 */
export default function CountryKbEditPage() {
  const { t } = useTranslation();
  const { isNew, isLoading, form, saveMutation, onSubmit, itemData } = useEditPage<CountryForm>({
    resourceKey: "country-kb-item",
    listKey: "country-kb",
    apiPath: "/api/admin/country-requirements",
    defaultValues: EMPTY,
    navigateTo: "/country-kb",
    transformOnLoad: (r) => ({
      countryCode: r.countryCode ?? "",
      countryName: r.countryName ?? "",
      visaPolicy: r.visaPolicy ?? "",
      visaFreeDays: r.visaFreeDays ?? null,
      visaFee: r.visaFee ?? "",
      visaUrl: r.visaUrl ?? "",
      visaNotes: r.visaNotes ?? "",
      passportMinValidityMonths: r.passportMinValidityMonths ?? null,
      vaccinationRequired: r.vaccinationRequired ?? "",
      healthNotes: r.healthNotes ?? "",
      arrivalCard: r.arrivalCard ?? "",
      arrivalCardUrl: r.arrivalCardUrl ?? "",
      arrivalCardWindowDays: r.arrivalCardWindowDays ?? null,
      arrivalCardNotes: r.arrivalCardNotes ?? "",
      arrivalCardGroupMax: r.arrivalCardGroupMax ?? null,
      // The API stores artifacts as string[]; the form edits them as comma-separated text.
      arrivalCardArtifactsText: (() => {
        const arts = (r as unknown as { arrivalCardArtifacts?: unknown }).arrivalCardArtifacts;
        return Array.isArray(arts) ? arts.join(", ") : "";
      })(),
      arrivalCardChannel: r.arrivalCardChannel ?? "manual",
      arrivalCardAutoEnabled: r.arrivalCardAutoEnabled ?? false,
      visaFilingLeadDays: r.visaFilingLeadDays ?? null,
      seasonality: r.seasonality ?? null,
      currencyInfo: r.currencyInfo ?? "",
      plugs: r.plugs ?? "",
      tipping: r.tipping ?? "",
      safetyNotes: r.safetyNotes ?? "",
      practicalNotes: r.practicalNotes ?? "",
      isActive: r.isActive ?? true,
    }),
    transformOnSave: (f) => ({
      ...f,
      countryCode: f.countryCode.toUpperCase(),
      visaPolicy: f.visaPolicy || null,
      // Nullable ints: ?? preserves null (|| would coerce it to 0 and fail .min checks).
      visaFreeDays: f.visaFreeDays ?? null,
      passportMinValidityMonths: f.passportMinValidityMonths ?? null,
      arrivalCard: f.arrivalCard || null,
      arrivalCardWindowDays: f.arrivalCardWindowDays ?? null,
      arrivalCardGroupMax: f.arrivalCardGroupMax ?? null,
      arrivalCardArtifactsText: undefined,
      arrivalCardArtifacts: f.arrivalCardArtifactsText
        ? f.arrivalCardArtifactsText
            .split(",")
            .map((a) => a.trim())
            .filter(Boolean)
        : null,
      visaFilingLeadDays: f.visaFilingLeadDays ?? null,
    }),
  });

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = form;

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="border-primary-500 h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/country-kb"
            className="rounded-lg p-2 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600 dark:text-gray-300" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {isNew
                ? t("catalog.countryKb.edit.newTitle")
                : `${itemData?.countryName ?? t("catalog.countryKb.edit.fallbackTitle")}`}
            </h1>
            <p className="mt-1 text-gray-600 dark:text-gray-400">
              {t("catalog.countryKb.edit.subtitle")}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <SaveSuccessIndicator isSuccess={saveMutation.isSuccess} />
          <button type="submit" form="ckb-form" disabled={isSubmitting} className="btn-primary">
            {isSubmitting ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" /> {t("common.actions.saving")}
              </>
            ) : (
              <>
                <Save className="h-5 w-5" /> {t("common.actions.save")}
              </>
            )}
          </button>
        </div>
      </div>

      <ErrorBanner error={saveMutation.error} />

      <form id="ckb-form" onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="card space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
            <div>
              <label className="label">{t("catalog.countryKb.edit.countryCode")}</label>
              <input
                {...register("countryCode", {
                  required: t("validation.required"),
                  minLength: { value: 2, message: t("catalog.countryKb.edit.twoLetters") },
                  maxLength: { value: 2, message: t("catalog.countryKb.edit.twoLetters") },
                })}
                className="input font-mono uppercase"
                placeholder="VN"
                maxLength={2}
              />
              {errors.countryCode && (
                <p className="mt-1 text-sm text-red-500">{errors.countryCode.message}</p>
              )}
            </div>
            <div className="sm:col-span-2">
              <label className="label">{t("catalog.countryKb.edit.countryName")}</label>
              <input
                {...register("countryName", { required: t("validation.required") })}
                className="input"
                placeholder="Vietnam"
              />
              {errors.countryName && (
                <p className="mt-1 text-sm text-red-500">{errors.countryName.message}</p>
              )}
            </div>
            <div className="flex items-end pb-2">
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <input type="checkbox" {...register("isActive")} className="h-4 w-4 rounded" />
                {t("common.status.active")}
              </label>
            </div>
          </div>
        </div>

        <div className="card space-y-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t("catalog.countryKb.edit.visaTitle")}
          </h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
            <div>
              <label className="label">{t("catalog.countryKb.edit.policy")}</label>
              <select {...register("visaPolicy")} className="input">
                <option value="">{t("catalog.countryKb.edit.unknown")}</option>
                <option value="visa_free">{t("catalog.countryKb.edit.policyVisaFree")}</option>
                <option value="visa_on_arrival">
                  {t("catalog.countryKb.edit.policyOnArrival")}
                </option>
                <option value="e_visa">{t("catalog.countryKb.edit.policyEVisa")}</option>
                <option value="embassy">{t("catalog.countryKb.edit.policyEmbassy")}</option>
              </select>
            </div>
            <div>
              <label className="label">{t("catalog.countryKb.edit.visaFreeDays")}</label>
              <input
                type="number"
                {...register("visaFreeDays", { valueAsNumber: true })}
                className="input"
                placeholder="45"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="label">{t("catalog.countryKb.edit.fee")}</label>
              <input
                {...register("visaFee")}
                className="input"
                placeholder={t("catalog.countryKb.edit.feePlaceholder")}
              />
            </div>
          </div>
          <div>
            <label className="label">{t("catalog.countryKb.edit.officialUrl")}</label>
            <input {...register("visaUrl")} className="input" placeholder="https://…" />
          </div>
          <div>
            <label className="label">{t("common.fields.notes")}</label>
            <Controller
              name="visaNotes"
              control={control}
              render={({ field }) => (
                <RichTextEditor
                  content={field.value || ""}
                  onChange={field.onChange}
                  placeholder={t("catalog.countryKb.edit.visaNotesPlaceholder")}
                />
              )}
            />
          </div>
        </div>

        <div className="card space-y-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t("catalog.countryKb.edit.passportHealthTitle")}
          </h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label className="label">{t("catalog.countryKb.edit.passportMinValidity")}</label>
              <input
                type="number"
                {...register("passportMinValidityMonths", { valueAsNumber: true })}
                className="input"
                placeholder="6"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="label">{t("catalog.countryKb.edit.vaccination")}</label>
              <input
                {...register("vaccinationRequired")}
                className="input"
                placeholder={t("catalog.countryKb.edit.vaccinationPlaceholder")}
              />
            </div>
          </div>
          <div>
            <label className="label">{t("catalog.countryKb.edit.healthNotes")}</label>
            <textarea {...register("healthNotes")} rows={2} className="input" />
          </div>
        </div>

        <div className="card space-y-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t("catalog.countryKb.edit.arrivalCardTitle")}
          </h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
            <div>
              <label className="label">{t("common.fields.type")}</label>
              <select {...register("arrivalCard")} className="input">
                <option value="">{t("catalog.countryKb.edit.unknown")}</option>
                <option value="none">{t("catalog.countryKb.edit.arrivalNone")}</option>
                <option value="on_arrival">{t("catalog.countryKb.edit.arrivalOnArrival")}</option>
                <option value="online">{t("catalog.countryKb.edit.arrivalOnline")}</option>
              </select>
            </div>
            <div>
              <label className="label">{t("catalog.countryKb.edit.opensDaysBefore")}</label>
              <input
                type="number"
                {...register("arrivalCardWindowDays", { valueAsNumber: true })}
                className="input"
                placeholder="7"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="label">{t("catalog.countryKb.edit.formUrl")}</label>
              <input {...register("arrivalCardUrl")} className="input" placeholder="https://…" />
            </div>
          </div>
          <div>
            <label className="label">{t("common.fields.notes")}</label>
            <textarea {...register("arrivalCardNotes")} rows={2} className="input" />
          </div>
          <div className="border-t border-gray-200 pt-4 dark:border-gray-700">
            <h3 className="mb-1 text-sm font-semibold text-gray-900 dark:text-white">
              {t("catalog.countryKb.edit.entryEngineTitle")}
            </h3>
            <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
              {t("catalog.countryKb.edit.entryEngineHint")}
            </p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
              <div>
                <label className="label">{t("catalog.countryKb.edit.groupSubmission")}</label>
                <input
                  type="number"
                  {...register("arrivalCardGroupMax", { valueAsNumber: true })}
                  className="input"
                  placeholder="10"
                />
                <p className="mt-1 text-xs text-gray-500">
                  {t("catalog.countryKb.edit.groupSubmissionHint")}
                </p>
              </div>
              <div>
                <label className="label">{t("catalog.countryKb.edit.visaTaskOpens")}</label>
                <input
                  type="number"
                  {...register("visaFilingLeadDays", { valueAsNumber: true })}
                  className="input"
                  placeholder="30"
                />
                <p className="mt-1 text-xs text-gray-500">
                  {t("catalog.countryKb.edit.visaTaskOpensHint")}
                </p>
              </div>
              <div>
                <label className="label">{t("catalog.countryKb.edit.filingChannel")}</label>
                <select {...register("arrivalCardChannel")} className="input">
                  <option value="manual">{t("catalog.countryKb.edit.channelManual")}</option>
                  <option value="browser">{t("catalog.countryKb.edit.channelBrowser")}</option>
                </select>
              </div>
              <div>
                <label className="label">{t("catalog.countryKb.edit.extraArtifacts")}</label>
                <input
                  {...register("arrivalCardArtifactsText")}
                  className="input"
                  placeholder="selfie, health_declaration"
                />
                <p className="mt-1 text-xs text-gray-500">
                  {t("catalog.countryKb.edit.commaSeparated")}
                </p>
              </div>
            </div>
            <label className="mt-3 flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                {...register("arrivalCardAutoEnabled")}
                className="h-4 w-4 rounded"
              />
              {t("catalog.countryKb.edit.autoFiling")}
            </label>
          </div>
        </div>

        <div className="card space-y-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t("catalog.countryKb.edit.seasonalityTitle")}
          </h2>
          <p className="-mt-2 text-sm text-gray-500 dark:text-gray-400">
            {t("catalog.countryKb.edit.seasonalityHint")}
          </p>
          <Controller
            name="seasonality"
            control={control}
            render={({ field }) => (
              <SeasonalityEditor value={field.value} onChange={field.onChange} />
            )}
          />
        </div>

        <div className="card space-y-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t("catalog.countryKb.edit.practicalTitle")}
          </h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label className="label">{t("common.fields.currency")}</label>
              <input
                {...register("currencyInfo")}
                className="input"
                placeholder={t("catalog.countryKb.edit.currencyPlaceholder")}
              />
            </div>
            <div>
              <label className="label">{t("catalog.countryKb.edit.plugs")}</label>
              <input {...register("plugs")} className="input" placeholder="A/C/F, 220 V" />
            </div>
            <div>
              <label className="label">{t("catalog.countryKb.edit.tipping")}</label>
              <input
                {...register("tipping")}
                className="input"
                placeholder={t("catalog.countryKb.edit.tippingPlaceholder")}
              />
            </div>
          </div>
          <div>
            <label className="label">{t("catalog.countryKb.edit.safetyNotes")}</label>
            <textarea {...register("safetyNotes")} rows={2} className="input" />
          </div>
          <div>
            <label className="label">{t("catalog.countryKb.edit.practicalNotes")}</label>
            <Controller
              name="practicalNotes"
              control={control}
              render={({ field }) => (
                <RichTextEditor
                  content={field.value || ""}
                  onChange={field.onChange}
                  placeholder={t("catalog.countryKb.edit.practicalNotesPlaceholder")}
                />
              )}
            />
          </div>
        </div>
      </form>
    </div>
  );
}
